import { useEffect, useState } from 'react';
import BillingService from '../../services/BillingService';
import Invoice from '../../types/Invoice';

export interface UseInvoiceRefreshParams {
    limit?: number;
    starting_after?: string;
    ending_before?: string;
}

export interface UseInvoicesProps {
    pageSize?: number;
}

export default function useInvoices({ pageSize = 10 }: UseInvoicesProps = {}) {
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [invoices, setInvoices] = useState<Invoice[] | undefined>(undefined);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [firstId, setFirstId] = useState<string | null>(null);
    const [lastId, setLastId] = useState<string | null>(null);

    const load = async (params: UseInvoiceRefreshParams) => {
        try {
            const result = await BillingService.getInvoices({
                limit: pageSize,
                ...params,
            });
            setInvoices(result.invoices);
            setHasMore(result.has_more);
            setFirstId(result.first_id);
            setLastId(result.last_id);
            setError(null);
        } catch (e: any) {
            setError(e);
        }
    };

    const refresh = async (params: UseInvoiceRefreshParams) => {
        setUpdating(true);
        await load(params);
        setUpdating(false);
    };

    const loadMore = async (customPageSize?: number) => {
        if (!lastId || !hasMore || updating) return;

        const loadPageSize = customPageSize || pageSize;
        setUpdating(true);
        try {
            const result = await BillingService.getInvoices({
                limit: loadPageSize,
                starting_after: lastId,
            });

            // Append new invoices to existing ones
            setInvoices((prevInvoices) => [
                ...(prevInvoices || []),
                ...result.invoices,
            ]);
            setHasMore(result.has_more);
            setLastId(result.last_id);
            if (!firstId) {
                setFirstId(result.first_id);
            }
            setError(null);
        } catch (e: any) {
            setError(e);
        }
        setUpdating(false);
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await load({});
            setLoading(false);
        };

        init();
    }, []);

    return {
        invoices,
        refresh,
        loadMore,
        loading,
        updating,
        error,
        hasMore,
        lastId,
        firstId,
    };
}
